import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Dashboard from './pages/Dashboard';
import ExperimentResults from './pages/ExperimentResults';
import ExperimentCreate from './pages/ExperimentCreate';
import GscOAuthWizard from './pages/GscOAuthWizard';
import { BingOAuthWizard } from './pages/BingOAuthWizard';
import { GscCredentialsManagement } from './pages/GscCredentialsManagement';
import JobMonitoring from './pages/JobMonitoring';
import { OpportunitiesHub } from './pages/opportunities/OpportunitiesHub';
import { GA4SetupWizard } from './pages/ga4/GA4SetupWizard';
import { DataHealthDashboard } from './pages/health/DataHealthDashboard';
import { ChangeHistoryPage } from './pages/changes/ChangeHistoryPage';
import PagesListPage from './pages/PagesListPage';
import { WebToolsConfiguration } from './pages/webtools/WebToolsConfiguration';
import ExperimentForm from './pages/experiments/ExperimentForm';
import ExperimentDetailView from './pages/experiments/ExperimentDetailView';
import ValidationDetailPage from './pages/validation/ValidationDetailPage';
import ValidationHistoryPage from './pages/validation/ValidationHistoryPage';
import ReadinessIndicatorPage from './pages/ReadinessIndicatorPage';
import LinkImpactPage from './pages/LinkImpactPage';
import DistributionSignalPage from './pages/DistributionSignalPage';
import InsightExplainabilityPage from './pages/InsightExplainabilityPage';
import ActionWorkspacePage from './pages/ActionWorkspacePage';
import OCIDashboardPage from './pages/OCIDashboardPage';
import MomentumPage from './pages/MomentumPage';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { OAuthCallbackPage } from './pages/OAuthCallback';
import OAuthSuccessPage from './pages/OAuthSuccessPage';
import DataDisclaimerPage from './pages/DataDisclaimerPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ForceChangePasswordPage } from './pages/ForceChangePasswordPage';
import AnomalyDashboard from './pages/AnomalyDashboard';
import AnalyticsJobMonitoring from './pages/AnalyticsJobMonitoring';
import AnalyticsIndex from './pages/analytics/AnalyticsIndex';
import AnalyticsIndexNew from './pages/analytics/AnalyticsIndexNew';
import ChangeDetectionAnalyticsPage from './pages/analytics/ChangeDetectionAnalyticsPage';
import MomentumAnalyticsPage from './pages/analytics/MomentumAnalyticsPage';
import OpportunityAnalyticsPage from './pages/analytics/OpportunityAnalyticsPage';
import SerpAnalyticsPage from './pages/analytics/SerpAnalyticsPage';
import Ga4AnalyticsPage from './pages/analytics/Ga4AnalyticsPage';
import RecommendationsAnalyticsPage from './pages/analytics/RecommendationsAnalyticsPage';
import OciListPage from './pages/analytics/OciListPage';
import OciAnalyticsPage from './pages/analytics/OciAnalyticsPage';
import HtmlDiffToolPage from './pages/analytics/HtmlDiffToolPage';
import ChangeDetectionConfigPage from './pages/analytics/ChangeDetectionConfigPage';
import ChangeDetectionIndex from './pages/analytics/ChangeDetectionIndex';
import AttentionDashboard from './pages/analytics/AttentionDashboard';
import GrowthOpportunities from './pages/analytics/GrowthOpportunities';
import CustomerDashboard from './pages/analytics/CustomerDashboard';
import AgencyDashboard from './pages/analytics/AgencyDashboard';
import EarlyStageDashboard from './pages/early-stage/EarlyStageDashboard';
import ReadinessPillarDetail from './pages/early-stage/ReadinessPillarDetail';
import TechnicalCrawlingPillarPage from './pages/early-stage/TechnicalCrawlingPillarPage';
import AiSearchabilityPillarPage from './pages/early-stage/AiSearchabilityPillarPage';
import PageReadinessView from './pages/early-stage/PageReadinessView';
import PageReadinessDetail from './pages/early-stage/PageReadinessDetail';
import TimelinePage from './pages/early-stage/TimelinePage';
import SiteReadinessHistoryPage from './pages/early-stage/SiteReadinessHistoryPage';
import ModeTransitionPage from './pages/early-stage/ModeTransitionPage';
import LocalBusinessPage from './pages/early-stage/LocalBusinessPage';
import GlossaryPage from './pages/GlossaryPage';
import OrganizationListPage from './pages/OrganizationListPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
import BillingPage from './pages/BillingPage';
// Customer Webtools Pages
import {
  CustomerWebtoolsDashboard,
  CustomerPerformancePage,
  CustomerAnalyticsPage,
  CustomerLocalBusinessPage,
  CustomerUrlCoveragePage,
  CustomerSitemapPage,
  CustomerContentAnalysisPage,
  CustomerLinksManagementPage,
  CustomerInternalLinksPage,
  CoreWebVitalsPage,
  CustomerSiteAuditPage,
  CustomerPageAuditPage,
  CustomerAuditTrackingPage,
  CustomerAuditRecommendationsPage,
} from './pages/customer';
import { useAuthStore } from './store/auth.store';
import AppLayout from './components/AppLayout';
import TenantSwitcherScreen from './pages/TenantSwitcherScreen';
// Public branding tool pages (no auth required)
import ToolsLandingPage from './pages/tools/ToolsLandingPage';
import WebsiteHealthCheckPage from './pages/tools/WebsiteHealthCheckPage';
import PageSeoAnalyzerPage from './pages/tools/PageSeoAnalyzerPage';
import RobotsTxtValidatorPage from './pages/tools/RobotsTxtValidatorPage';
import SitemapValidatorPage from './pages/tools/SitemapValidatorPage';
import GscOwnerVerificationPage from './pages/tools/GscOwnerVerificationPage';
import SchemaDetectorPage from './pages/tools/SchemaDetectorPage';
import AiCrawlingReadinessPage from './pages/tools/AiCrawlingReadinessPage';
import Ga4OnPageValidationPage from './pages/tools/Ga4OnPageValidationPage';
import ConsolidatedReportPage from './pages/tools/ConsolidatedReportPage';
import ContactUsPage from './pages/tools/ContactUsPage';
import { CircularProgress } from '@mui/material';
import { getDefaultAnalyticsPath, isAgencyScopedProfile } from './utils/analyticsNavigation';

function App() {
  const { isAuthenticated, passwordMustChange, _hasHydrated, user } = useAuthStore();
  const hasAgreedToDisclaimer = localStorage.getItem('data_disclaimer_agreed') === 'true';
  // Agency admin or multi-tenant customer: tenant not yet selected when allowedTenantIds is present
  const isAgencyAdmin = isAgencyScopedProfile(user);
  const isMultiTenantCustomer = user?.profile === 'customer' && (user?.allowedTenants?.length ?? 0) > 1;
  const needsTenantSelection = (isAgencyAdmin || isMultiTenantCustomer) && (user?.allowedTenants?.length ?? 0) > 0 && !user?.tenantConfirmed;

  // Allow /oauth-success to render immediately without waiting for auth hydration
  // This is needed for popup windows during OAuth flow
  const isOAuthSuccessRoute = window.location.pathname === '/oauth-success';

  // Wait for auth store to hydrate from localStorage before rendering (except for OAuth success page)
  if (!_hasHydrated && !isOAuthSuccessRoute) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<Navigate to="/login" replace />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage />} />
        <Route path="/oauth-success" element={<OAuthSuccessPage />} />
        <Route path="/force-change-password" element={isAuthenticated ? <ForceChangePasswordPage /> : <Navigate to="/login" replace />} />
        <Route path="/disclaimer" element={isAuthenticated ? <DataDisclaimerPage /> : <Navigate to="/login" replace />} />
        <Route path="/select-tenant" element={isAuthenticated ? <TenantSwitcherScreen /> : <Navigate to="/login" replace />} />

        {/* --- Public branding tools (no auth required) --- */}
        <Route path="/tools" element={<ToolsLandingPage />} />
        <Route path="/tools/website-health-check" element={<WebsiteHealthCheckPage />} />
        <Route path="/tools/page-seo-analyzer" element={<PageSeoAnalyzerPage />} />
        <Route path="/tools/robots-txt-validator" element={<RobotsTxtValidatorPage />} />
        <Route path="/tools/sitemap-validator" element={<SitemapValidatorPage />} />
        <Route path="/tools/gsc-owner-verification-check" element={<GscOwnerVerificationPage />} />
        <Route path="/tools/schema-detector" element={<SchemaDetectorPage />} />
        <Route path="/tools/ai-crawling-readiness" element={<AiCrawlingReadinessPage />} />
        <Route path="/tools/ga4-onpage-validation" element={<Ga4OnPageValidationPage />} />
        <Route path="/tools/consolidated-report" element={<ConsolidatedReportPage />} />
        <Route path="/tools/contact" element={<ContactUsPage />} />
        <Route
          path="/"
          element={
            isAuthenticated
              ? (passwordMustChange
                  ? <Navigate to="/force-change-password" replace />
                  : needsTenantSelection
                  ? <Navigate to="/select-tenant" replace />
                  : (hasAgreedToDisclaimer ? <AppLayout /> : <Navigate to="/disclaimer" replace />))
              : <Navigate to="/login" replace />
          }
        >
          <Route index element={<Navigate to={getDefaultAnalyticsPath(user)} replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="pages" element={<PagesListPage />} />
          <Route path="experiments" element={<ExperimentResults />} />
          <Route path="experiments/create" element={<ExperimentCreate />} />
          <Route path="experiments/new" element={<ExperimentForm />} />
          <Route path="experiments/:experimentId" element={<ExperimentDetailView />} />
          <Route path="opportunities" element={<OpportunitiesHub />} />
          <Route path="opportunities/striking-distance" element={<OpportunitiesHub />} />
          <Route path="opportunities/ctr-anomaly" element={<OpportunitiesHub />} />
          <Route path="opportunities/intent-gaps" element={<OpportunitiesHub />} />
          <Route path="opportunities/cannibalization" element={<OpportunitiesHub />} />
          <Route path="actions" element={<Navigate to="/actions-next" replace />} />
          <Route path="actions-next" element={<ActionWorkspacePage />} />
          <Route path="actions-next/:actionId" element={<ActionWorkspacePage />} />
          <Route path="data-health" element={<DataHealthDashboard />} />
          <Route path="webtools/config" element={<WebToolsConfiguration />} />
          <Route path="pages/changes/by-url" element={<ChangeHistoryPage />} />
          <Route path="pages/:pageId/changes" element={<ChangeHistoryPage />} />
          <Route path="pages/:pageId/link-impact" element={<LinkImpactPage />} />
          <Route path="pages/:pageId/distribution-signals" element={<DistributionSignalPage />} />
          <Route path="pages/:pageId/momentum" element={<MomentumPage />} />
          <Route path="insights/:insightId/explainability" element={<InsightExplainabilityPage />} />
          <Route path="validation/runs/:runId" element={<ValidationDetailPage />} />
          <Route path="validation/history" element={<ValidationHistoryPage />} />
          <Route path="readiness-confidence/:experimentId" element={<ReadinessIndicatorPage />} />
          <Route path="pages/oci/by-url" element={<OCIDashboardPage />} />
          <Route path="pages/:pageId/oci" element={<OCIDashboardPage />} />
          <Route path="ga4/setup" element={<GA4SetupWizard />} />
          <Route path="ga4/callback" element={<GA4SetupWizard />} />
          <Route path="webtools/ga4/callback" element={<GA4SetupWizard />} />
          <Route path="gsc-oauth/:siteId" element={<GscOAuthWizard />} />
          <Route path="oauth/callback" element={<GscOAuthWizard />} />
          <Route path="oauth/bing" element={<BingOAuthWizard />} />
          <Route path="bing-oauth/callback" element={<BingOAuthWizard />} />
          <Route path="gsc-credentials" element={<GscCredentialsManagement />} />
          <Route path="admin/jobs" element={<JobMonitoring />} />
          <Route path="admin/analytics-jobs" element={<AnalyticsJobMonitoring />} />
          {/* Analytics section */}
          <Route path="analytics" element={isAgencyScopedProfile(user) ? <AgencyDashboard /> : <CustomerDashboard />} />
          <Route path="analytics/agency" element={<AgencyDashboard />} />
          <Route path="analytics/customer" element={<CustomerDashboard />} />
          <Route path="analytics/customer/:siteId" element={<CustomerDashboard />} />
          <Route path="analytics/home" element={<AnalyticsIndexNew />} />
          <Route path="analytics/old" element={<AnalyticsIndex />} />
          <Route path="analytics/attention" element={<AttentionDashboard />} />
          <Route path="analytics/opportunities" element={<GrowthOpportunities />} />
          <Route path="analytics/anomalies" element={<AnomalyDashboard />} />
          <Route path="analytics/oci" element={<OciListPage />} />
          <Route path="analytics/oci/detail" element={<OciAnalyticsPage />} />
          <Route path="analytics/change-detection-overview" element={<ChangeDetectionIndex />} />
          <Route path="analytics/change-detection" element={<ChangeDetectionAnalyticsPage />} />
          <Route path="analytics/html-diff" element={<HtmlDiffToolPage />} />
          <Route path="analytics/change-detection/config" element={<ChangeDetectionConfigPage />} />
          <Route path="analytics/momentum" element={<MomentumAnalyticsPage />} />
          <Route path="analytics/opportunity" element={<OpportunityAnalyticsPage />} />
          <Route path="analytics/serp" element={<SerpAnalyticsPage />} />
          <Route path="analytics/ga4" element={<Ga4AnalyticsPage />} />
          <Route path="analytics/recommendations" element={<RecommendationsAnalyticsPage />} />
          {/* Early-Stage Mode */}
          <Route path="early-stage" element={<EarlyStageDashboard />} />
          <Route path="early-stage/readiness/technical_hygiene" element={<TechnicalCrawlingPillarPage />} />
          <Route path="early-stage/readiness/ai_searchability" element={<AiSearchabilityPillarPage />} />
          <Route path="early-stage/readiness/:pillarId" element={<ReadinessPillarDetail />} />
          <Route path="early-stage/readiness-history" element={<SiteReadinessHistoryPage />} />
          <Route path="early-stage/pages" element={<PageReadinessView />} />
          <Route path="early-stage/pages/:pageId" element={<PageReadinessDetail />} />
          <Route path="early-stage/transition" element={<ModeTransitionPage />} />
          <Route path="early-stage/timeline" element={<TimelinePage />} />
          <Route path="early-stage/local-business" element={<LocalBusinessPage />} />
          {/* Glossary & Help */}
          <Route path="glossary" element={<GlossaryPage />} />
          {/* Organization */}
          <Route path="organization" element={<OrganizationListPage />} />
          <Route path="organization/:orgId" element={<OrganizationDetailPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="organization/site/:siteId" element={<Navigate to="/organization" replace />} />
          {/* Backward-compat route redirect */}
          <Route path="sites" element={<Navigate to="/organization" replace />} />
          {/* Customer Webtools Section */}
          <Route path="customer" element={<CustomerWebtoolsDashboard />} />
          <Route path="customer/performance" element={<CustomerPerformancePage />} />
          <Route path="customer/analytics" element={<CustomerAnalyticsPage />} />
          <Route path="customer/local-business" element={<CustomerLocalBusinessPage />} />
          <Route path="customer/url-coverage" element={<CustomerUrlCoveragePage />} />
          <Route path="customer/sitemap" element={<CustomerSitemapPage />} />
          <Route path="customer/content-analysis" element={<CustomerContentAnalysisPage />} />
          <Route path="customer/links" element={<CustomerLinksManagementPage />} />
          <Route path="customer/internal-links" element={<CustomerInternalLinksPage />} />
          <Route path="customer/core-web-vitals" element={<CoreWebVitalsPage />} />
          <Route path="customer/audit/site" element={<CustomerSiteAuditPage />} />
          <Route path="customer/audit/page" element={<CustomerPageAuditPage />} />
          <Route path="customer/audit/tracking" element={<CustomerAuditTrackingPage />} />
          <Route path="customer/audit/recommendations" element={<CustomerAuditRecommendationsPage />} />
          {/* Backward-compat route redirect */}
          <Route path="anomalies" element={<Navigate to="/analytics/anomalies" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

export default App;
